
import { FC, useState } from "react";
import { Domain } from "@/types/domain";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { FileText, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

interface DomainCardProps {
  domain: Domain;
}

const getDomainKnowledgeBases = (symbol: string) => {
  // Sample knowledge bases for each domain
  return [
    { title: "Best Practices Guide", url: "#" },
    { title: "Implementation Framework", url: "#" },
    { title: "Case Studies Collection", url: "#" }
  ];
};

const DomainCard: FC<DomainCardProps> = ({ domain }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleKnowledgeBaseClick = (url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(url, "_blank");
  };

  return (
    <div className="flex flex-col">
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div 
              className="bg-primary text-primary-foreground p-3 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:bg-black hover:shadow-lg flex flex-col items-center relative h-20 min-w-0" 
              onClick={handleClick}
            >
              <div className="text-sm font-bold text-center leading-tight flex-1 flex items-center justify-center px-1">{domain.symbol}</div>
              
              <div className="absolute right-2 top-2">
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-white text-black border border-gray-200 max-w-[400px] whitespace-pre-line">
            <p className="font-semibold mb-2">{domain.name}</p>
            <p className="text-sm">Representative Knowledge Bases:</p>
            <p className="text-xs mt-1">{domain.categories}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {isExpanded && (
        <div className="mt-2 bg-white rounded-lg shadow-md p-3 border border-gray-200 z-10">
          <h3 className="text-sm font-semibold mb-2">Knowledge Bases</h3>
          <div className="space-y-2">
            {getDomainKnowledgeBases(domain.symbol).map((kb, index) => (
              <div 
                key={index} 
                className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer text-xs" 
                onClick={e => handleKnowledgeBaseClick(kb.url, e)}
              >
                <FileText className="h-3 w-3 text-primary flex-shrink-0" />
                <span className="flex-1">{kb.title}</span>
                <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainCard;
